/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Combobox, Flex, Text, TextField } from '@servicetitan/anvil2';
import { CalculateSuperHeatRequestDto, GasType } from '../../models/calculator.dto';
import { FieldState, FormState } from 'formstate';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const CalculateSuperHeatForm: FC<{
    className?: string;
    onSubmit?: (params: CalculateSuperHeatRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            gas_type: new FieldState<{ id: GasType; name: string } | undefined>(
                undefined
            ).validators(value => !value && 'Required'),
            pressure: new FieldState<string | undefined>(undefined),
            temperature: new FieldState<string | undefined>(undefined),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        if (!formState.$.gas_type.$) {
            return;
        }
        const params: CalculateSuperHeatRequestDto = {
            tool_name: 'calculate-super-heat',
            tool_input: {
                gas_type: formState.$.gas_type.$!.id,
                pressure: Number(formState.$.pressure.$),
                temperature: Number(formState.$.temperature.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <Combobox.Select
                items={store.gasTypes}
                itemToString={item => (item ? item.name : '')}
                itemToKey={item => (item ? item.id : null)}
                selectedItem={formState.$.gas_type.value}
                onChange={formState.$.gas_type.onChange}
                disableClearSelection
                style={{ width: '100%', maxWidth: '100%' }}
            >
                <Combobox.SearchField
                    required
                    label="Refrigerant Type"
                    error={formState.$.gas_type.error}
                />
                <Combobox.Content>
                    {({ items }) => (
                        <Combobox.List>
                            {items.map((item, i) => (
                                <Combobox.Item key={item.id} item={item} index={i}>
                                    {item.name}
                                </Combobox.Item>
                            ))}
                        </Combobox.List>
                    )}
                </Combobox.Content>
            </Combobox.Select>
            <TextField
                label="Suction Line Pressure (PSIG)"
                type="number"
                value={formState.$.pressure.value}
                onChange={e => formState.$.pressure.onChange(e.target.value)}
            />
            <TextField
                label="Suction Line Temperature (°F)"
                type="number"
                value={formState.$.temperature.value}
                onChange={e => formState.$.temperature.onChange(e.target.value)}
            />

            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
